/**
 * @file ShopifyErrorBoundary.tsx
 * What: Catches uncaught React runtime crashes inside the Shopify App context and renders a clean, user-friendly fallback screen with error details.
 * Transport / data-flow: Pure client-side UI error interceptor.
 *
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { InlineStack, Banner, Button, Text } from '@shopify/polaris'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { InsightsAcceptedAttempt } from '../insightsAttempt'
import { reportInsightsFailure } from '../insightsDiagnostics'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { describeErrorForDiagnostics } from '../merchantErrors'
import { buildSupportViewUrl } from '../resultsNavigation'

interface Props {
  children?: ReactNode
  diagnosticAttempt?: InsightsAcceptedAttempt
  isDiagnosticActive?: () => boolean
  /** App Bridge token source for the authenticated crash report. `useAppBridge()` throws without App Bridge, so it is always present. */
  shopify: ReturnType<typeof useAppBridge>
}

interface State {
  hasError: boolean
  error: Error | null
}

/** Scrubbed type name for a received value — never the value itself. */
function describeActualValue(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

/**
 * Extracts schema identity plus scrubbed paths from a Zod-like failure.
 * Values never leave this helper — paths are dotted keys, received are type names.
 */
export function describeZodDiagnostic(
  error: unknown,
  schemaId = 'study-builder-editor',
): { schema: string; paths: string[]; received: string[] } {
  const holder = error as { schemaId?: unknown; issues?: unknown; cause?: unknown } | null | undefined
  const schema = typeof holder?.schemaId === 'string' && holder.schemaId.length > 0 ? holder.schemaId : schemaId
  const issues = holder?.issues
  if (!Array.isArray(issues)) {
    if (holder?.cause !== undefined && holder.cause !== error) return describeZodDiagnostic(holder.cause, schema)
    return { schema, paths: [], received: [describeActualValue(error)] }
  }
  const paths: string[] = []
  const received: string[] = []
  for (const issue of issues) {
    if (typeof issue !== 'object' || issue === null) continue
    const record = issue as { path?: unknown; received?: unknown }
    if (Array.isArray(record.path)) {
      const dotted = record.path.map((segment) => String(segment)).join('.')
      paths.push(dotted.length > 0 ? dotted : '(root)')
    } else {
      paths.push('(root)')
    }
    if (typeof record.received === 'string') {
      received.push(record.received)
    } else if ('received' in issue) {
      received.push(describeActualValue(record.received))
    } else {
      received.push('unknown')
    }
  }
  return { schema, paths, received }
}

export class ShopifyErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error inside Shopify App:', describeErrorForDiagnostics(error), errorInfo)
    try {
      const attempt = this.props.diagnosticAttempt
      if (attempt) {
        if (this.props.isDiagnosticActive && !this.props.isDiagnosticActive()) return
        reportInsightsFailure(this.props.shopify, attempt.scopeKey, {
          ...attempt,
          occurredAt: new Date().toISOString(),
          phase: 'render',
          outcome: 'error',
          failureClass: 'render_contract',
          issueCode: 'render_crash',
          issuePath: 'presentation',
          phases: [],
        })
        return
      }
      const diagnostic = describeZodDiagnostic(error)
      const search = new URLSearchParams(globalThis.window?.location.search ?? '')
      const setupId = search.get('setupId') ?? undefined
      const input = {
        schema: diagnostic.schema,
        paths: diagnostic.paths,
        ...(setupId ? { setupId } : {}),
      }
      reportClientDiagnostic(this.props.shopify, input)
    } catch (reportError) {
      console.error('Failed to report client diagnostic', reportError)
    }
  }

  public override render() {
    if (this.state.hasError) {
      const params = new URLSearchParams(globalThis.window?.location.search ?? '')
      const supportUrl = buildSupportViewUrl({
        setupId: params.get('setupId') || undefined,
        packetId: params.get('packetId') || undefined,
        runId: params.get('runId') || undefined,
        latestStepKey: params.get('latestStepKey') || undefined,
      })
      return (
        <s-page heading={shopifyComponentCopy.shopifyErrorBoundary.appError001}>
          <Banner title={shopifyComponentCopy.shopifyErrorBoundary.somethingWentWrong002} tone="critical">
            <InlineStack gap="400" align="space-between" blockAlign="center">
              <Text as="p">{shopifyComponentCopy.shopifyErrorBoundary.theTestfeedAppEncounteredA003}</Text>
              <Button url={supportUrl}>{shopifyComponentCopy.settingsPage.contactSupport009}</Button>
            </InlineStack>
          </Banner>
        </s-page>
      )
    }

    return this.props.children
  }
}
